import React, { useState } from "react";
import "../../styles/Global.css";
import styles from "../../pages/Login.module.css";
import InputField from "./InputField";
import AuthLinks from "./AuthLinks";
import { useAuthContext } from "../../contexts/AuthContext";
import { detectInAppBrowser, inAppBrowserLabel } from "../../utils/inAppBrowser";

interface LoginFormProps {
  onSubmit?: (email: string, password: string) => void;
  isLoading?: boolean;
  error?: string | null;
  returnTo?: string | null;
}

/**
 * Login form component with email and password fields
 * Uses glassmorphism styling for a modern, translucent appearance
 *
 * @param {LoginFormProps} props - Component properties
 * @returns {JSX.Element} - Rendered login form component
 */
const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading = false,
  error = null,
  returnTo = null,
}) => {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  // Get Google auth helper from context
  const { signInWithGoogle } = useAuthContext();
  // Google blocks OAuth inside app browsers such as TikTok's.
  const [inAppBrowser] = useState(() => detectInAppBrowser());

  /**
   * Validates the form inputs
   * @returns {boolean} - Whether the form is valid
   */
  const validateForm = (): boolean => {
    const newErrors = {
      email: "",
      password: "",
    };

    // Simple email validation
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);

    // Form is valid if there are no error messages
    return !Object.values(newErrors).some((error) => error);
  };

  /**
   * Handles form submission
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm() && onSubmit) {
      onSubmit(email, password);
    }
  };

  /**
   * Handle Google sign in (placeholder for now)
   */
  const handleGoogleSignIn = () => {
    // No await needed – Supabase redirects immediately
    signInWithGoogle("login", returnTo);
  };

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Password visibility toggle icon
  const eyeIcon = (
    <svg
      onClick={togglePasswordVisibility}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.eyeIcon}
    >
      {showPassword ? (
        // Eye open icon
        <path
          d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"
          fill="currentColor"
        />
      ) : (
        // Eye closed icon
        <path
          d="M12 19.5C7 19.5 2.73 16.39 1 12C2.73 7.61 7 4.5 12 4.5C17 4.5 21.27 7.61 23 12C21.27 16.39 17 19.5 12 19.5ZM12 17C14.76 17 17 14.76 17 12C17 9.24 14.76 7 12 7C9.24 7 7 9.24 7 12C7 14.76 9.24 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9ZM3.71 3.29L20.7 20.29L19.29 21.7L2.29 4.71L3.71 3.29Z"
          fill="currentColor"
        />
      )}
    </svg>
  );

  return (
    <div className={styles.loginForm}>
      <h2 className={styles.loginTitle}>
        Sign In
      </h2>

      <p className={styles.loginSubtitle}>
        Please sign in to continue to your account
      </p>

      {/* Display auth error if present */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <InputField
          label="Email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />

        <InputField
          label="Password"
          type={showPassword ? "text" : "password"}
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          icon={eyeIcon}
        />

        <button
          type="submit"
          disabled={isLoading}
          className={styles.submitButton}
        >
          {isLoading ? "SIGNING IN..." : "SIGN IN"}
        </button>

        {inAppBrowser ? (
          <p className={styles.inAppNotice} role="note">
            Google sign-in isn't available inside {inAppBrowserLabel(inAppBrowser)}.
            Use your email and password above, or open this page in Safari or Chrome.
          </p>
        ) : (
          <>
            {/* Or divider */}
            <div className={styles.dividerContainer}>
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>
                OR
              </span>
              <div className={styles.dividerLine} />
            </div>

            {/* Google Sign In Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className={styles.googleButton}
            >
              {/* Google Icon */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                className={styles.googleIcon}
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign in with Google
            </button>
          </>
        )}
      </form>

      <AuthLinks returnTo={returnTo} />
    </div>
  );
};

export default LoginForm;
